/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useRef } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useSelector, useDispatch, batch } from 'react-redux';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';
import ResizeObserver from 'react-resize-observer';
import { openPopupWidget } from 'react-calendly';

import SportScienceHome from './SportScienceHome';
import WearablesDashboard from '../wearables-dashboard/WearablesDashboard';
import LoadMonitoringDashboard from '../load-monitoring-dashboard/LoadMonitoringDashboard';
import ForcePlateDashboard from '../force-plate-dashboard/ForcePlateDashboard';
import SportCoachDashboard from '../sport-coach-dashboard/SportCoachDashboard';
import HabitTrackingDashboard from '../habit-tracking-dashboard/HabitTrackingDashboard';
import VolumeTrackerDashboard from '../volume-tracker-dashboard/VolumeTrackerDashboard';
import Layout from '../../shared/components/Layout/Layout';
import getAllUrlParams from '../../shared/utils/getAllUrlParams';
import InstantPaymentModalContainer from './components/presentational/InstantPaymentModalContainer';
import BillingDashboardWrapper from './components/presentational/BillingDashboardWrapper';
import SideBar from './components/presentational/SideBar';
import UpgradeModalContainer from './components/presentational/UpgradeModalContainer';
import MarketingModalContainer from './components/presentational/MarketingModalContainer';
import BookDemoModalContainer from './components/presentational/BookDemoModalContainer';
import { BoxShadow } from '../../shared/GlobalStyles';
import DeleteDashboardModalContainer from '../sport-coach-dashboard/components/presentational/DeleteDashboardModalContainer';
import AboutDashboardModalContainer from '../sport-coach-dashboard/components/presentational/AboutDashboardModalContainer';
import EditDashboardModalContainer from '../sport-coach-dashboard/components/presentational/EditDashboardModalContainer';
import ShareDashboardModalContainer from '../sport-coach-dashboard/components/presentational/ShareDashboardModalContainer';
import SaveDashboardModalContainer from '../sport-coach-dashboard/components/presentational/SaveDashboardModalContainer';
import TriOptionModalContainer from '../sport-coach-dashboard/components/presentational/TriOptionModalContainer';
import UnsavedDashModalContainer from '../sport-coach-dashboard/components/presentational/UnsavedDashModalContainer';
import DuplicateDashboardModalContainer from '../sport-coach-dashboard/components/presentational/DuplicateDashboardModalContainer';
import SCLegendInfoModal from '../sport-coach-dashboard/components/presentational/SCLegendInfoModal';
import SharedTemplateUnsavedModal from './components/presentational/sharedTemplateComponents/SharedTemplateUnsavedModal';
import { fetchRegionStatusToggle } from '../recovery-radar/ducks/RecoveryRadarActions';
import RecoveryRadar from '../recovery-radar/RecoveryRadar';
import {
  SportsScienceModal,
  SportsScienceErrorModal,
} from './components/presentational/SportsScienceModal';
import {
  fetchSubscriptionPackage,
  setIsInstantPaymentModalShowing,
  setIsUpdatedUpgradeModalShowing,
  setIsUpgradeModalShowing,
  setSelectedPackage,
  setSideBarOpen,
} from './ducks/sportsScienceActions';
import LearnMoreModalContainer from './components/presentational/LearnMoreModalContainer';
import UpdatedUpgradeModal from '../../shared/_TBComponents/OnboardingModal'; // USING ONBOARDING MODAL FOR IMPROVED UPGRADE MODAL EXPERIENCE
import {
  subscriptionFlowTracker,
  subscriptionFlowTrackerDemo,
} from '../../shared/utils/amplitudeHelper';
import VBTDashboard from '../vbt-dashboard/VBTDashboard';
import corePackage from './components/presentational/packages';
import SharedTemplateAboutModal from './components/presentational/sharedTemplateComponents/SharedTemplateAboutModal';
import SharedTemplateDeleteModal from './components/presentational/sharedTemplateComponents/SharedTemplateDeleteModal';
import SharedTemplateDuplicateModal from './components/presentational/sharedTemplateComponents/SharedTemplateDuplicateModal';
import SharedTemplateEditModal from './components/presentational/sharedTemplateComponents/SharedTemplateEditModal';
import SharedTemplateSaveModal from './components/presentational/sharedTemplateComponents/SharedTemplateSaveModal';
import SharedTemplateShareModal from './components/presentational/sharedTemplateComponents/SharedTemplateShareModal';
import SharedTemplateTriOptionModal from './components/presentational/sharedTemplateComponents/SharedTemplateTriOptionModal';

const FlexContainer = styled('div')`
  display: flex;
  height: 100%;
`;

const ForcePlateWrapper = styled('div')`
  display: flex;
  flex: 1;
  margin-top: ${(props) => (props.hideNav ? '0' : '49px')};
  transition: all 0.5s ease;
  background: #f5f5f5;
  overflow-x: hidden;
  overflow-y: auto;
  @media screen and (max-width: 768px) {
    width: ${(props) => (!props.isSideBarActive ? '100%' : '0%')};
  }

  @media print {
    @page {
      size: landscape;
      margin: 15mm;
    }
  }
`;

const ModuleText = styled('div')`
  color: #ff8533;
  font-weight: bold;
`;

const PageWrapper = styled('div')`
  display: flex;
  height: ${(props) => (props.hideNav ? '100%' : 'calc(100% - 49px)')};
  flex: 1;
  margin-top: ${(props) => (props.hideNav ? '0' : '49px')};
  transition: all 0.5s ease;
  @media only screen and (max-width: 768px) {
    height: calc(100%);
  }
  background: #f5f5f5;
  overflow-y: auto;
  @media screen and (max-width: 768px) {
    width: ${(props) => (!props.isSideBarActive ? '100%' : '0%')};
  }
`;

const SCDashboardWrapper = styled('div')`
  display: flex;
  flex: 1;
  margin-top: ${(props) => (props.hideNav ? '0' : '49px')};
  transition: all 0.5s ease;
  background: #f5f5f5;
  overflow-x: hidden;
  overflow-y: auto;
  @media screen and (max-width: 768px) {
    width: ${(props) => (!props.isSideBarActive ? '100%' : '0%')};
  }

  @media print {
    @page {
      size: landscape;
      margin: 15mm;
    }
  }
`;

const SideBarReturnButton = styled('div')`
  align-items: center;
  background: white;
  border-radius: 50px;
  bottom: 20px;
  box-shadow: ${BoxShadow};
  cursor: pointer;
  display: none;
  font-family: 'Nunito Sans';
  font-size: 10px;
  height: 35px;
  justify-content: center;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  -webkit-transform: translateX(-50%);
  width: 140px;
  z-index: 100;
  @media screen and (max-width: 714px) {
    display: flex;
  }
`;

const SportsScience = ({ match }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const fpAboutModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.aboutModalShowing,
  );
  const fpDeleteModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.deleteModalShowing,
  );
  const fpDuplicateModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.duplicateModalShowing,
  );
  const fpEditModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.editModalShowing,
  );
  const fpSaveModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.saveModalShowing,
  );
  const fpShareModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.shareModalShowing,
  );
  const fpTriOptionModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.triOptionModalShowing,
  );
  const habitTrackingPageWrapperRef = useRef(null);
  const isLegendInfoModalShowing = useSelector(
    (state) => state.sportCoachDashboard.ui.isLegendInfoModalShowing,
  );
  const isSideBarOpen = useSelector((state) => state.sportsScience.data.isSideBarOpen);
  const isUpdatedUpgradeModalShowing = useSelector(
    (state) => state.sportsScience.data.isUpdatedUpgradeModalShowing,
  );
  const lmAboutModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.aboutModalShowing,
  );
  const lmDeleteModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.deleteModalShowing,
  );
  const lmDuplicateModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.duplicateModalShowing,
  );
  const lmEditModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.editModalShowing,
  );
  const lmSaveModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.saveModalShowing,
  );
  const lmShareModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.shareModalShowing,
  );
  const lmTriOptionModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.triOptionModalShowing,
  );
  const recentlyCreatedHabit = useSelector(
    (state) => state.habitTracking.data.recentlyCreatedHabit,
  );
  const selectedPackage = useSelector((state) => state.sportsScience.data.selectedPackage);
  const subscriptionPackage = useSelector((state) => state.sportsScience.data.subscriptionPackage);
  const subscriptionStatus = useSelector((state) => state.sportsScience.data.subscriptionStatus);
  const unsavedFPDashModalShowing = useSelector(
    (state) => state.forcePlateDashboard.ui.unsavedModalShowing,
  );
  const unsavedLMDashModalShowing = useSelector(
    (state) => state.loadMonitoringDashboard.ui.unsavedModalShowing,
  );
  const unsavedVBTDashModalShowing = useSelector(
    (state) => state.vbtDashboard.ui.unsavedModalShowing,
  );
  const unsavedVTDashModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.unsavedModalShowing,
  );
  const vbtAboutModalShowing = useSelector((state) => state.vbtDashboard.ui.aboutModalShowing);
  const vbtDeleteModalShowing = useSelector((state) => state.vbtDashboard.ui.deleteModalShowing);
  const vbtDuplicateModalShowing = useSelector(
    (state) => state.vbtDashboard.ui.duplicateModalShowing,
  );
  const vbtEditModalShowing = useSelector((state) => state.vbtDashboard.ui.editModalShowing);
  const vbtSaveModalShowing = useSelector((state) => state.vbtDashboard.ui.saveModalShowing);
  const vbtShareModalShowing = useSelector((state) => state.vbtDashboard.ui.shareModalShowing);
  const vtAboutModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.aboutModalShowing,
  );
  const vtDeleteModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.deleteModalShowing,
  );
  const vtDuplicateModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.duplicateModalShowing,
  );
  const vtEditModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.editModalShowing,
  );
  const vtOptionModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.optionModalShowing,
  );
  const vtSaveModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.saveModalShowing,
  );
  const vtShareModalShowing = useSelector(
    (state) => state.volumeTrackerDashboard.ui.shareModalShowing,
  );

  const dispatch = useDispatch();

  const [containerWidth, setContainerWidth] = useState(0);
  const [isSideBarActive, setIsSideBarActive] = useState(true);

  // Check for hideNav query parameter
  const urlParams = getAllUrlParams(window.location.href, false);
  const hideNav = urlParams.hidenav === 'true';

  const showDisclaimer = !subscriptionStatus?.packageSubscriptionStatus?.isSubscribed
    && !selectedPackage?.core;

  const handleBookDemoClick = () => {
    // Tracks which modal a user booked a demo from
    subscriptionFlowTrackerDemo('Modal', selectedPackage);
    openPopupWidget({
      url: 'https://calendly.com/teambuildr/15-minute-sports-science-hub',
    });
  };

  const handleDisclaimerClick = () => {
    // Close the modal showing add-on feature content
    dispatch(setIsUpdatedUpgradeModalShowing(false));
    // Set delay equal to modal unmount transition time
    // Reopen modal with core subscription content
    setTimeout(() => {
      batch(() => {
        dispatch(setSelectedPackage(corePackage));
        dispatch(
          fetchSubscriptionPackage(
            currentUser.accountCode,
            1,
          ),
        );
        dispatch(setIsUpgradeModalShowing(true));
      });
    }, 300);
  };

  const handleUpgradeClick = () => {
    if (currentUser?.userSettings?.billing === 0) {
      // billing access is disabled
      window.open(
        `${process.env.PHP_APP_URL || 'https://app.teambuildr.com'}/billing`,
      );
    } else {
      // Tracks which modal a user started the upgrade process from
      subscriptionFlowTracker(
        'Upgrade Modal Started',
        selectedPackage.title,
        'none',
      );
      batch(() => {
        dispatch(setIsUpdatedUpgradeModalShowing(false));
        if (subscriptionPackage?.redirect) {
          window.open(subscriptionPackage?.url);
        } else {
          dispatch(setIsInstantPaymentModalShowing(true));
        }
      });
    }
  };

  // Shared function used on individual dashboard page to open informational modal
  const handleViewInfoModal = (moduleData) => {
    // Amplitude tracking
    subscriptionFlowTracker(
      'Modal Viewed',
      moduleData.legacyTitle || moduleData.title,
      'none',
    );
    batch(() => {
      dispatch(setSelectedPackage(moduleData));
      dispatch(setIsUpgradeModalShowing(true));
    });
  };

  /** track if side bar is open in global state */
  useEffect(() => {
    dispatch(setSideBarOpen(isSideBarActive));
  }, [isSideBarActive]);

  /** track if new habit was added and scroll to bottom of page */
  useEffect(() => {
    if (recentlyCreatedHabit) {
      habitTrackingPageWrapperRef.current?.scrollTo({
        top: habitTrackingPageWrapperRef.current?.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [recentlyCreatedHabit]);

  // Fetch the state of the region/status toggle for recovery radar
  useEffect(() => {
    if (currentUser?.accountCode) {
      dispatch(fetchRegionStatusToggle(currentUser?.accountCode));
    }
  }, [currentUser?.accountCode]);

  return (
    <Layout fullWidth>
      <ResizeObserver
        onResize={(rect) => {
          setContainerWidth(rect?.width);
          if (!isSideBarOpen && rect?.width > 714) {
            setIsSideBarActive(true);
          }
          if (isSideBarOpen && rect?.width <= 714) {
            setIsSideBarActive(false);
          }
        }}
      />
      <div className='overflow-scroll-boi'>
        <InstantPaymentModalContainer />
        <LearnMoreModalContainer setIsSideBarActive={setIsSideBarActive} />
        {/* Force Plate Upgrade Modal */}
        <UpgradeModalContainer />
        {/* All other upgrade modals */}
        <UpdatedUpgradeModal
          badgeText={selectedPackage?.core === 1 ? 'INCLUDED IN CORE' : 'ADD ON FEATURES'}
          content={selectedPackage?.content || []}
          dashName={selectedPackage?.title || ''}
          disclaimerClick={showDisclaimer ? handleDisclaimerClick : null}
          isActive={isUpdatedUpgradeModalShowing}
          onClose={() => dispatch(setIsUpdatedUpgradeModalShowing(false))}
          onPrimaryAction={handleUpgradeClick}
          onSecondaryAction={handleBookDemoClick}
          primaryButtonText='UPGRADE'
          secondaryButtonText='BOOK A DEMO'
          fullWidth
          isUpgrade
        />
        <MarketingModalContainer />
        <BookDemoModalContainer />

        {/* Force Plate Modals */}
        {fpAboutModalShowing && (
        <SharedTemplateAboutModal dashboard='Force Plate' />
        )}
        {fpDeleteModalShowing && (
        <SharedTemplateDeleteModal dashboard='Force Plate' legacyName='Force Plate' />
        )}
        {fpDuplicateModalShowing && (
        <SharedTemplateDuplicateModal dashboard='Force Plate' legacyName='Force Plate' />
        )}
        {fpEditModalShowing && (
          <SharedTemplateEditModal dashboard='Force Plate' legacyName='Force Plate' />
        )}
        {fpSaveModalShowing && (
          <SharedTemplateSaveModal dashboard='Force Plate' legacyName='Force Plate' />
        )}
        {fpShareModalShowing && (
        <SharedTemplateShareModal dashboard='Force Plate' legacyName='Force Plate' />
        )}
        {fpTriOptionModalShowing && (
        <SharedTemplateTriOptionModal dashboard='Force Plate' legacyName='Force Plate' />
        )}
        {unsavedFPDashModalShowing && (
        <SharedTemplateUnsavedModal dashboard='Force Plate' legacyName='Force Plate' />
        )}

        {/* KPI Tracker Modals */}
        <AboutDashboardModalContainer />
        <DeleteDashboardModalContainer />
        <DuplicateDashboardModalContainer />
        <EditDashboardModalContainer />
        <SaveDashboardModalContainer />
        {isLegendInfoModalShowing && <SCLegendInfoModal />}
        <ShareDashboardModalContainer />
        <TriOptionModalContainer />
        <UnsavedDashModalContainer />

        {/* Load Monitoring Modals */}
        {lmAboutModalShowing && (
        <SharedTemplateAboutModal dashboard='Load Monitoring' />
        )}
        {lmDeleteModalShowing && (
        <SharedTemplateDeleteModal dashboard='Load Monitoring' legacyName='Load Monitoring' />
        )}
        {lmDuplicateModalShowing && (
        <SharedTemplateDuplicateModal dashboard='Load Monitoring' legacyName='Load Monitoring' />
        )}
        {lmEditModalShowing && (
        <SharedTemplateEditModal dashboard='Load Monitoring' legacyName='Load Monitoring' />
        )}
        {lmSaveModalShowing && (
        <SharedTemplateSaveModal dashboard='Load Monitoring' legacyName='Load Monitoring' />
        )}
        {lmShareModalShowing && (
        <SharedTemplateShareModal dashboard='Load Monitoring' legacyName='Load Monitoring' />
        )}
        {lmTriOptionModalShowing && (
        <SharedTemplateTriOptionModal dashboard='Load Monitoring' legacyName='Load Monitoring' />
        )}
        {unsavedLMDashModalShowing && (
          <SharedTemplateUnsavedModal dashboard='Load Monitoring' legacyName='Load Monitoring' />
        )}

        {/* VBT Modals */}
        {vbtAboutModalShowing && (
        <SharedTemplateAboutModal dashboard='VBT' />
        )}
        {vbtDeleteModalShowing && (
        <SharedTemplateDeleteModal dashboard='VBT' legacyName='VBT' />
        )}
        {vbtDuplicateModalShowing && (
        <SharedTemplateDuplicateModal dashboard='VBT' legacyName='VBT' />
        )}
        {vbtEditModalShowing && (
        <SharedTemplateEditModal dashboard='VBT' legacyName='VBT' />
        )}
        {vbtSaveModalShowing && (
        <SharedTemplateSaveModal dashboard='VBT' legacyName='VBT' />
        )}
        {vbtShareModalShowing && (
        <SharedTemplateShareModal dashboard='VBT' legacyName='VBT' />
        )}
        {unsavedVBTDashModalShowing && (
          <SharedTemplateUnsavedModal dashboard='VBT' legacyName='VBT' />
        )}

        {/* Volume Tracker Modals */}
        {vtAboutModalShowing && (
        <SharedTemplateAboutModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
        {vtDeleteModalShowing && (
        <SharedTemplateDeleteModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
        {vtDuplicateModalShowing && (
        <SharedTemplateDuplicateModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
        {vtEditModalShowing && (
        <SharedTemplateEditModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
        {vtSaveModalShowing && (
        <SharedTemplateSaveModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
        {vtShareModalShowing && (
        <SharedTemplateShareModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
        {vtOptionModalShowing && (
        <SharedTemplateTriOptionModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
        {unsavedVTDashModalShowing && (
          <SharedTemplateUnsavedModal dashboard='Volume Tracker' legacyName='Volume Tracker' />
        )}
      </div>
      <Helmet>
        <title>AMS | TeamBuildr</title>
      </Helmet>
      <FlexContainer>
        <SideBar
          isSideBarActive={isSideBarActive}
          setIsSideBarActive={setIsSideBarActive}
          containerWidth={containerWidth}
        />
        <Switch>
          <Route exact path={match.path}>
            <PageWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <SportScienceHome
                isSideBarActive={isSideBarActive}
                setIsSideBarActive={setIsSideBarActive}
              />
            </PageWrapper>
          </Route>
          <Route path={`${match.path}/wearables-dashboard`}>
            <PageWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <WearablesDashboard handleViewInfoModal={handleViewInfoModal} />
            </PageWrapper>
          </Route>
          <Route path={`${match.path}/load-monitoring-dashboard`}>
            <SCDashboardWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <LoadMonitoringDashboard handleViewInfoModal={handleViewInfoModal} />
            </SCDashboardWrapper>
          </Route>
          <Route path={`${match.path}/kpi-tracker`}>
            <SCDashboardWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <SportCoachDashboard handleViewInfoModal={handleViewInfoModal} />
            </SCDashboardWrapper>
          </Route>
          <Route path={`${match.path}/force-plate-dashboard`}>
            <ForcePlateWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <ForcePlateDashboard handleViewInfoModal={handleViewInfoModal} />
            </ForcePlateWrapper>
          </Route>
          <Route path={`${match.path}/body-heatmap`}>
            <ForcePlateWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <RecoveryRadar />
            </ForcePlateWrapper>
          </Route>
          <Route path={`${match.path}/habit-tracker`}>
            <ForcePlateWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <HabitTrackingDashboard />
            </ForcePlateWrapper>
          </Route>
          <Route path={`${match.path}/volume-tracker`}>
            <ForcePlateWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <VolumeTrackerDashboard />
            </ForcePlateWrapper>
          </Route>
          <Route path={`${match.path}/vbt-dashboard`}>
            <ForcePlateWrapper isSideBarActive={isSideBarActive} hideNav={hideNav}>
              <VBTDashboard />
            </ForcePlateWrapper>
          </Route>
          <Route path={`${match.path}/billing`}>
            <BillingDashboardWrapper />
          </Route>
          <Redirect to={match.path} />
        </Switch>
        {!isSideBarActive ? (
          <SideBarReturnButton onClick={() => setIsSideBarActive(true)}>
            <ModuleText>RETURN TO SIDEBAR</ModuleText>
          </SideBarReturnButton>
        ) : null}
        <SportsScienceModal />
        <SportsScienceErrorModal />
      </FlexContainer>
    </Layout>
  );
};

SportsScience.propTypes = { match: PropTypes.instanceOf(Object).isRequired };

export default SportsScience;
